import { useEffect, useMemo, useRef, useState } from "react";
import { onStyleReady } from "../lib/mapready";
import { createPortal } from "react-dom";
import { LngLatBounds, Marker, Popup, type GeoJSONSource, type Map as MLMap, type MapLayerMouseEvent } from "maplibre-gl";
import type { Feature, FeatureCollection } from "geojson";
import type { Device } from "../lib/supabase";
import {
  fetchHistory, fetchHistoryRange, fijiClock, fijiDayClock, fijiToday, fmtDuration, positionAt, shiftDay,
  type History, type HistPoint, type Trip,
} from "../lib/history";
import { RouteLoader, useLoaderPhase } from "../ui/RouteLoader";
import HistoryBar from "./history/HistoryBar";
import type { Zone } from "../lib/org";
import { zonesAt, zoneStack } from "../lib/geo";
import { markerParts, setSilhouette } from "../lib/vehicleMarker";

const EMPTY_LINE: Feature = { type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: [] } };
const EMPTY_FC: FeatureCollection = { type: "FeatureCollection", features: [] };

type Props = {
  map: MLMap | null;
  devices: Device[];
  deviceId: string | null;
  onDeviceChange: (id: string) => void;
  barTarget: HTMLElement | null;
  /** Map container: the loading overlay covers the map only (not the side panel). */
  overlayTarget?: HTMLElement | null;
  /** Quick-menu request: last 24 h (rolling) / today / yesterday (calendar days). `n` changes on every request. */
  preset?: { kind: "24h" | "today" | "yesterday"; n: number } | null;
  /** zones, for the "Zone" column and point popups (innermost › outer) */
  zones?: Zone[];
};

export default function HistoryPanel({ map, devices, deviceId, onDeviceChange, barTarget, overlayTarget, preset, zones = [] }: Props) {
  const [day, setDay] = useState(fijiToday());
  // V3.4.0: nothing is loaded until the user picks a period (1/2/3/7 days or Last 24 h) — or a quick-menu preset.
  const [span, setSpan] = useState<number | null>(null);
  const [lastHour, setLastHour] = useState<number | null>(null); // set → showing the rolling last 24 h ending at this time
  useEffect(() => {
    if (!preset) return;
    if (preset.kind === "24h") { setSpan(null); setLastHour(Date.now()); return; }
    setLastHour(null); setSpan(1);
    setDay(preset.kind === "today" ? fijiToday() : shiftDay(fijiToday(), -1));
  }, [preset?.n]); // eslint-disable-line
  const chosen = lastHour != null || span != null;
  const clock = (span ?? 1) > 1 || lastHour ? fijiDayClock : fijiClock;
  const [data, setData] = useState<History | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tripIdx, setTripIdx] = useState<number | null>(null);
  const [playT, setPlayT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(60);
  const zonesRef = useRef<Zone[]>(zones);
  zonesRef.current = zones;
  const marker = useRef<Marker | null>(null);
  const phase = useLoaderPhase(loading, !!error, "history");

  const points = data?.points ?? [];
  const t0 = points[0]?.[2] ?? 0;
  const t1 = points[points.length - 1]?.[2] ?? 0;

  // ---- map layers (created once per mount, removed on unmount)
  useEffect(() => {
    if (!map) return;
    const add = () => {
      if (map.getSource("hist-route")) return;
      map.addSource("hist-route", { type: "geojson", data: EMPTY_LINE });
      map.addSource("hist-trip", { type: "geojson", data: EMPTY_LINE });
      map.addSource("hist-stops", { type: "geojson", data: EMPTY_FC });
      map.addLayer({ id: "hist-route-casing", type: "line", source: "hist-route",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#ffffff", "line-width": 7, "line-opacity": 0.9 } });
      map.addLayer({ id: "hist-route", type: "line", source: "hist-route",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#257AF4", "line-width": 4 } });
      map.addLayer({ id: "hist-trip", type: "line", source: "hist-trip",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#0b3f94", "line-width": 6 } });
      map.addLayer({ id: "hist-stops", type: "circle", source: "hist-stops",
        paint: {
          "circle-radius": 6, "circle-stroke-width": 2, "circle-stroke-color": "#ffffff",
          "circle-color": ["match", ["get", "kind"], "start", "#16a34a", "end", "#dc2626", "#eab308"],
        } });
      // direction arrows along the line
      if (!map.hasImage("tf-arrow")) map.addImage("tf-arrow", arrowImage(), { pixelRatio: 2 });
      map.addLayer({ id: "hist-arrows", type: "symbol", source: "hist-route",
        layout: { "symbol-placement": "line", "symbol-spacing": 90, "icon-image": "tf-arrow", "icon-size": ["interpolate", ["linear"], ["zoom"], 10, 0.9, 16, 1.3],
          "icon-allow-overlap": true, "icon-ignore-placement": true, "icon-rotation-alignment": "map", "icon-keep-upright": false } }, "hist-stops");
      // each recorded position (visible when zoomed in) + unconfirmed positions (grey)
      map.addSource("hist-points", { type: "geojson", data: EMPTY_FC });
      map.addSource("hist-suspect", { type: "geojson", data: EMPTY_FC });
      map.addLayer({ id: "hist-suspect", type: "circle", source: "hist-suspect",
        paint: { "circle-radius": 4, "circle-color": "#ffffff", "circle-stroke-color": "#9ca3af", "circle-stroke-width": 1.5, "circle-opacity": 0.8 } }, "hist-stops");
      map.addLayer({ id: "hist-points", type: "circle", source: "hist-points", minzoom: 14,
        paint: { "circle-radius": ["interpolate", ["linear"], ["zoom"], 14, 2.5, 18, 5], "circle-color": "#ffffff",
          "circle-stroke-color": "#257AF4", "circle-stroke-width": 1.5 } }, "hist-stops");
    };
    onStyleReady(map, add);

    const el = document.createElement("div");
    el.className = "pb-marker";
    marker.current = new Marker({ element: el, anchor: "center" });

    // click a point → speed / time popup
    const popup = new Popup({ closeButton: false, offset: 8, className: "pt-pop" });
    const onClick = (e: MapLayerMouseEvent) => {
      const f = e.features?.[0];
      if (!f) return;
      const p = f.properties as { t: number; kmh?: number; course?: number; suspect?: boolean };
      const [lng, lat] = (f.geometry as any).coordinates as [number, number];
      popup.setLngLat([lng, lat]).setHTML(p.suspect
        ? `<b>Unconfirmed position</b><br><span>${fijiDayClock(Number(p.t))}</span><br><small>Not used for the route or distance.</small>`
        : `<b>${p.kmh ?? 0} km/h</b> <span class="dir" style="--rot:${p.course ?? 0}deg">↑</span><br><span>${fijiDayClock(Number(p.t))}</span>${
          (() => { const zs = zonesAt(zonesRef.current, lat, lng); return zs.length ? `<br><small>${escapeHtml(zoneStack(zs))}</small>` : ""; })()}`).addTo(map);
    };
    const enter = () => { map.getCanvas().style.cursor = "pointer"; };
    const leave = () => { map.getCanvas().style.cursor = ""; };
    for (const id of ["hist-points", "hist-suspect"]) { map.on("click", id, onClick); map.on("mouseenter", id, enter); map.on("mouseleave", id, leave); }

    return () => {
      marker.current?.remove();
      marker.current = null;
      popup.remove();
      for (const id of ["hist-points", "hist-suspect"]) { map.off("click", id, onClick); map.off("mouseenter", id, enter); map.off("mouseleave", id, leave); }
      for (const id of ["hist-points", "hist-suspect", "hist-arrows", "hist-stops", "hist-trip", "hist-route", "hist-route-casing"]) if (map.getLayer(id)) map.removeLayer(id);
      for (const id of ["hist-points", "hist-suspect", "hist-stops", "hist-trip", "hist-route"]) if (map.getSource(id)) map.removeSource(id);
    };
  }, [map]);

  // ---- load
  async function load(id = deviceId, d = day, n = span ?? 1) {
    if (!id || !chosen) return;
    setLoading(true); setError(null); setPlaying(false); setTripIdx(null);
    try {
      const h = lastHour ? await fetchHistoryRange(id, new Date(lastHour - 24 * 3600_000).toISOString(), new Date(lastHour).toISOString())
        : await fetchHistory(id, d, n);
      setData(h);
      setPlayT(h.points[0]?.[2] ?? 0);
    } catch (e: any) {
      setData(null);
      setError(e?.message ?? "Could not load history");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { if (deviceId && chosen) load(deviceId, day, span ?? 1); /* eslint-disable-next-line */ }, [deviceId, day, span, lastHour]);
  // a new object or period clears what was shown until the new search runs
  useEffect(() => { if (!chosen) { setData(null); setError(null); } }, [chosen]);

  // ---- draw route + stops when data changes
  useEffect(() => {
    if (!map) return;
    const draw = () => {
      const route = map.getSource("hist-route") as GeoJSONSource | undefined;
      const stopsSrc = map.getSource("hist-stops") as GeoJSONSource | undefined;
      const tripSrc = map.getSource("hist-trip") as GeoJSONSource | undefined;
      if (!route || !stopsSrc || !tripSrc) return;
      const coords = points.map((p) => [p[0], p[1]]);
      route.setData({ ...EMPTY_LINE, geometry: { type: "LineString", coordinates: coords } });
      tripSrc.setData(EMPTY_LINE);
      const feats: Feature[] = (data?.stops ?? []).map((s) => ({
        type: "Feature", properties: { kind: "stop" }, geometry: { type: "Point", coordinates: [s.lng, s.lat] },
      }));
      if (coords.length) {
        feats.push({ type: "Feature", properties: { kind: "start" }, geometry: { type: "Point", coordinates: coords[0] } });
        feats.push({ type: "Feature", properties: { kind: "end" }, geometry: { type: "Point", coordinates: coords[coords.length - 1] } });
      }
      stopsSrc.setData({ type: "FeatureCollection", features: feats });
      (map.getSource("hist-points") as GeoJSONSource | undefined)?.setData({ type: "FeatureCollection", features: points.map((p) => ({
        type: "Feature", properties: { t: p[2], kmh: p[3], course: p[4] }, geometry: { type: "Point", coordinates: [p[0], p[1]] } })) });
      (map.getSource("hist-suspect") as GeoJSONSource | undefined)?.setData({ type: "FeatureCollection", features: (data?.suspect ?? []).map((p) => ({
        type: "Feature", properties: { t: p[2], suspect: true }, geometry: { type: "Point", coordinates: [p[0], p[1]] } })) });
      if (coords.length) fit(map, points, 300);
      if (coords.length && marker.current) marker.current.setLngLat(coords[0] as [number, number]).addTo(map);
      else marker.current?.remove();
    };
    if (map.getSource("hist-route")) draw(); else map.once("load", () => setTimeout(draw, 0));
  }, [map, data]);

  // ---- playback loop
  useEffect(() => {
    if (!playing || !points.length) return;
    let raf = 0, last = performance.now();
    const step = (now: number) => {
      const dt = now - last; last = now;
      setPlayT((t) => {
        const nt = t + dt * speed;
        if (nt >= t1) { setPlaying(false); return t1; }
        return nt;
      });
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [playing, speed, t1, points.length]);

  // ---- move playback marker
  const cur = useMemo(() => (points.length ? positionAt(points, playT) : null), [points, playT]);
  useEffect(() => {
    if (!cur || !marker.current) return;
    marker.current.setLngLat([cur[0], cur[1]]);
    const el = marker.current.getElement();
    el.dataset.moving = String(cur[2] >= 3);
    // V3.9.1: the device's silhouette, rotated with the route (keeps its heading while stopped)
    setSilhouette(markerParts(el, false).dot, devices.find((d) => d.id === deviceId)?.icon, cur[3], cur[2], 32);
  }, [cur]); // eslint-disable-line

  function selectTrip(i: number, trip: Trip) {
    setTripIdx(i);
    const a = Date.parse(trip.start), b = Date.parse(trip.end);
    const seg = points.filter((p) => p[2] >= a && p[2] <= b);
    const src = map?.getSource("hist-trip") as GeoJSONSource | undefined;
    src?.setData({ ...EMPTY_LINE, geometry: { type: "LineString", coordinates: seg.map((p) => [p[0], p[1]]) } });
    if (map && seg.length) fit(map, seg, 300);
    setPlayT(a);
    setPlaying(false);
  }

  const summary = useMemo(() => {
    const trips = data?.trips ?? [];
    return {
      km: trips.reduce((s, t) => s + t.distanceKm, 0),
      driveMin: trips.reduce((s, t) => s + t.durationMin, 0),
      maxKmh: trips.reduce((s, t) => Math.max(s, t.maxKmh), 0),
      trips: trips.length,
      stops: data?.stops.length ?? 0,
    };
  }, [data]);

  const isToday = day === fijiToday();
  // changing the day keeps the chosen number of days (from "Last 24 h" it switches to 1 day)
  function pickDay(d: string) { if (lastHour) { setLastHour(null); setSpan(1); } setDay(d); }

  return (
    <>
      <div className={`hist-controls ${loading ? "busy" : ""}`}>
        <label className="field">
          <span>Object</span>
          <select value={deviceId ?? ""} onChange={(e) => onDeviceChange(e.target.value)}>
            <option value="" disabled>Select an object…</option>
            {devices.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </label>
        <div className="day-row">
          <button className="btn-ghost sq" onClick={() => pickDay(shiftDay(day, -1))} aria-label="Previous day">‹</button>
          <input type="date" value={day} max={fijiToday()} onChange={(e) => { if (e.target.value) pickDay(e.target.value); }} />
          <button className="btn-ghost sq" onClick={() => pickDay(shiftDay(day, 1))} disabled={isToday} aria-label="Next day">›</button>
          {!isToday && <button className="btn-ghost" onClick={() => pickDay(fijiToday())}>Today</button>}
        </div>
        <div className="span-row" role="group" aria-label="How many days">
          {[1, 2, 3, 7].map((n) => (
            <button key={n} className={!lastHour && span === n ? "on" : ""} onClick={() => { setLastHour(null); setSpan(n); if (span === n && !lastHour) load(deviceId, day, n); }}>{n === 1 ? "1 day" : `${n} days`}</button>
          ))}
          <button className={lastHour ? "on" : ""} onClick={() => { setSpan(null); setLastHour(Date.now()); }} title="Rolling 24 hours up to now">Last 24 h</button>
          {(span ?? 1) > 1 && !lastHour && <span className="sub">ending {day}</span>}
        </div>
      </div>

      {!deviceId && <p className="muted pad">Choose an object to see where it has been.</p>}
      {deviceId && !chosen && <p className="muted pad">Pick a day and a period above (1 day, 2 days … or Last 24 h) to load the history.</p>}
      {loading && !phase && <p className="muted pad">Loading history…</p>}
      {overlayTarget && phase && createPortal(
        <div className="tf-loader-scrim">
          <RouteLoader phase={phase} kind="history"
            sub={phase === "ready" && data ? `${data.trips.length} trip${data.trips.length === 1 ? "" : "s"} · ${data.points.length} positions` : undefined} />
        </div>, overlayTarget)}
      {error && <div className="error pad">{error}</div>}

      {data && !loading && (
        <>
          <div className="hist-summary">
            <div><b className="mono">{summary.km.toFixed(1)}</b><span>km</span></div>
            <div><b className="mono">{fmtDuration(summary.driveMin)}</b><span>driving</span></div>
            <div><b className="mono">{summary.trips}</b><span>trips</span></div>
            <div><b className="mono">{summary.maxKmh}</b><span>max km/h</span></div>
          </div>
          {!points.length && <p className="muted pad">No positions recorded in this period.</p>}
          <ul className="list trips">
            {data.trips.map((t, i) => (
              <li key={t.start} className={tripIdx === i ? "sel" : ""} onClick={() => selectTrip(i, t)}>
                <div className="row1">
                  <span className="name mono">{clock(t.start)} – {fijiClock(t.end)}</span>
                  <span className="speed mono">{t.distanceKm.toFixed(1)} km</span>
                </div>
                <div className="row2">{fmtDuration(t.durationMin)} · avg {t.avgKmh} · max {t.maxKmh} km/h</div>
              </li>
            ))}
            {points.length > 0 && !data.trips.length && <li className="empty muted">No trips — the unit did not move in this period.</li>}
          </ul>
          <p className="fine pad">
            {data.rawCount} positions · {summary.stops} stops
            {data.dropped > 0 && <> · {data.dropped} unconfirmed (grey){" "}</>}
            {(data.noFix ?? 0) > 0 && <> · {data.noFix} without GPS fix</>}
            {points.length > 1 && <><br />Zoom in to see each recorded point — click one for its speed and time.</>}
          </p>
        </>
      )}

      {barTarget && points.length > 1 && data && deviceId && createPortal(
        <HistoryBar deviceId={deviceId} from={data.from} to={data.to} points={points} t0={t0} t1={t1}
          playT={playT} setPlayT={setPlayT} playing={playing} setPlaying={setPlaying} speed={speed} setSpeed={setSpeed}
          curKmh={cur ? cur[2] : 0} multiDay={(span ?? 1) > 1 || !!lastHour} zones={zones}
          onRow={(lng, lat) => map?.easeTo({ center: [lng, lat], zoom: Math.max(map.getZoom(), 15), duration: 400 })} />,
        barTarget,
      )}
    </>
  );
}

/** White-outlined arrowhead pointing right (MapLibre rotates it along the line). Drawn at 2× for sharp edges. */
function arrowImage() {
  const c = document.createElement("canvas");
  c.width = 36; c.height = 36;
  const g = c.getContext("2d")!;
  g.lineJoin = "round";
  g.beginPath(); g.moveTo(11, 8); g.lineTo(27, 18); g.lineTo(11, 28); g.lineTo(15, 18); g.closePath();
  g.strokeStyle = "#ffffff"; g.lineWidth = 5; g.stroke();
  g.fillStyle = "#0b3f94"; g.fill();
  return g.getImageData(0, 0, 36, 36);
}

function fit(map: MLMap, pts: HistPoint[], bottomPad: number) {
  if (!pts.length) return;
  const b = new LngLatBounds([pts[0][0], pts[0][1]], [pts[0][0], pts[0][1]]);
  for (const p of pts) b.extend([p[0], p[1]]);
  map.fitBounds(b, { padding: { top: 60, left: 60, right: 60, bottom: bottomPad }, maxZoom: 16, duration: 600 });
}

const escapeHtml = (v: string) => v.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
